import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Loader2, ArrowRight, MessageSquare, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = ["Interior Painting", "Exterior Painting", "Cabinet Painting & Refinishing", "Deck & Fence Staining", "Drywall Repair & Prep", "Color Consultation", "Multiple Services", "Other"];

const contactInfo = [
  { icon: Phone, label: "Call Us", value: "(236)868-1195", href: "tel:2368681195", color: "bg-[#f97316]" },
  { icon: Mail, label: "Email Us", value: "info@vanguardpm.ca", href: "mailto:info@vanguardpm.ca", color: "bg-[#0c2340]" },
  { icon: Clock, label: "Working Hours", value: "Mon - Fri: 8am - 6pm", href: null, color: "bg-[#0c2340]" },
  { icon: MapPin, label: "Service Area", value: "Greater Vancouver, BC", href: null, color: "bg-[#0c2340]" },
];

export const ContactPage = () => {
  const [tab, setTab] = useState("quote");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [quote, setQuote] = useState({ name: "", email: "", phone: "", service_type: "", project_description: "", preferred_date: "", address: "" });

  const submitContact = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success("Message sent! We'll get back to you soon.");
      setContact({ name: "", email: "", phone: "", message: "" });
      setSuccess(true);
    } catch { toast.error("Failed to send. Please try again."); }
    finally { setLoading(false); }
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, quote);
      toast.success("Quote request submitted! We'll contact you within 24 hours.");
      setQuote({ name: "", email: "", phone: "", service_type: "", project_description: "", preferred_date: "", address: "" });
      setSuccess(true);
    } catch { toast.error("Failed to submit. Please try again."); }
    finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="pt-24 bg-white" data-testid="contact-page">
        <section className="min-h-[70vh] flex items-center justify-center bg-[#0c2340]">
          <div className="text-center px-4">
            <div className="w-20 h-20 bg-[#f97316] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Thank You!
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-md mx-auto">
              We've received your message and will get back to you within 24 hours.
            </p>
            <a href="/" className="inline-flex items-center gap-2 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold px-8 py-4 rounded-lg transition-all">
              Back to Home <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-24 bg-white" data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-[#0c2340] overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#f97316]/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-[#f97316]/5 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-[#f97316] rounded-full"></span>
                <span className="text-white/80 font-semibold text-sm">Get In Touch</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Let's Start Your <span className="text-[#f97316]">Next Project</span>
              </h1>
              <p className="text-gray-400 text-lg leading-relaxed mb-10">
                Ready to transform your home? Get a free, no-obligation quote from our expert team. We respond within 24 hours.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {contactInfo.map((c) => (
                  <div key={c.label} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-4 hover:border-[#f97316]/30 transition-all" data-testid={`contact-info-${c.label}`}>
                    <div className={`w-11 h-11 ${c.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <c.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">{c.label}</p>
                      {c.href ? (
                        <a href={c.href} className="text-white font-bold text-sm hover:text-[#f97316] transition-colors">{c.value}</a>
                      ) : (
                        <p className="text-white font-bold text-sm">{c.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-2xl" data-testid="forms-section">
              {/* Tabs */}
              <div className="flex bg-gray-100 p-1.5 rounded-xl mb-8">
                <button onClick={() => setTab("quote")} data-testid="tab-quote"
                  className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    tab === "quote" ? "bg-[#f97316] text-white shadow-lg" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <CheckCircle className="w-4 h-4" /> Request Quote
                </button>
                <button onClick={() => setTab("contact")} data-testid="tab-contact"
                  className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    tab === "contact" ? "bg-[#f97316] text-white shadow-lg" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <MessageSquare className="w-4 h-4" /> Send Message
                </button>
              </div>

              {/* Quote Form */}
              {tab === "quote" && (
                <form onSubmit={submitQuote} className="space-y-4" data-testid="quote-form-container">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input data-testid="quote-name-input" value={quote.name} onChange={(e) => setQuote({...quote, name: e.target.value})} placeholder="Full Name *" required
                        className="w-full pl-10 pr-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all" />
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="email" data-testid="quote-email-input" value={quote.email} onChange={(e) => setQuote({...quote, email: e.target.value})} placeholder="Email *" required
                        className="w-full pl-10 pr-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="tel" data-testid="quote-phone-input" value={quote.phone} onChange={(e) => setQuote({...quote, phone: e.target.value})} placeholder="Phone *" required
                        className="w-full pl-10 pr-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all" />
                    </div>
                    <Select value={quote.service_type} onValueChange={(v) => setQuote({...quote, service_type: v})}>
                      <SelectTrigger data-testid="quote-service-select" className="h-[50px] bg-gray-50 border-gray-200 rounded-lg text-sm">
                        <SelectValue placeholder="Select Service *" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">{services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input type="date" data-testid="quote-date-input" value={quote.preferred_date} onChange={(e) => setQuote({...quote, preferred_date: e.target.value})}
                      className="w-full px-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all text-gray-500" />
                    <div className="relative">
                      <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input data-testid="quote-address-input" value={quote.address} onChange={(e) => setQuote({...quote, address: e.target.value})} placeholder="Address"
                        className="w-full pl-10 pr-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all" />
                    </div>
                  </div>
                  <textarea data-testid="quote-description-input" value={quote.project_description} onChange={(e) => setQuote({...quote, project_description: e.target.value})} placeholder="Tell us about your project... *" rows={4} required
                    className="w-full px-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all resize-none" />
                  <button type="submit" disabled={loading} data-testid="quote-submit-btn"
                    className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white font-bold py-4 rounded-lg text-base shadow-lg shadow-orange-500/25 transition-all hover:shadow-orange-500/40 disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4" /> Submit Quote Request</>}
                  </button>
                </form>
              )}

              {/* Contact Form */}
              {tab === "contact" && (
                <form onSubmit={submitContact} className="space-y-4" data-testid="contact-form-container">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input data-testid="contact-name-input" value={contact.name} onChange={(e) => setContact({...contact, name: e.target.value})} placeholder="Full Name *" required
                        className="w-full pl-10 pr-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all" />
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="email" data-testid="contact-email-input" value={contact.email} onChange={(e) => setContact({...contact, email: e.target.value})} placeholder="Email *" required
                        className="w-full pl-10 pr-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all" />
                    </div>
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="tel" data-testid="contact-phone-input" value={contact.phone} onChange={(e) => setContact({...contact, phone: e.target.value})} placeholder="Phone (Optional)"
                      className="w-full pl-10 pr-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all" />
                  </div>
                  <textarea data-testid="contact-message-input" value={contact.message} onChange={(e) => setContact({...contact, message: e.target.value})} placeholder="How can we help? *" rows={5} required
                    className="w-full px-4 py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-sm focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all resize-none" />
                  <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                    className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white font-bold py-4 rounded-lg text-base shadow-lg shadow-orange-500/25 transition-all hover:shadow-orange-500/40 disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Message</>}
                  </button>
                </form>
              )}

              <div className="flex items-center justify-center gap-2 mt-6">
                <div className="h-px flex-1 bg-gray-200"></div>
                <span className="text-gray-400 text-xs">or call us directly</span>
                <div className="h-px flex-1 bg-gray-200"></div>
              </div>
              <a href="tel:2368681195" className="flex items-center justify-center gap-2 mt-3 text-[#f97316] font-bold text-sm hover:text-[#ea580c] transition-colors">
                <Phone className="w-4 h-4" /> (236)868-1195
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Map / CTA Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-2xl p-8 lg:p-12 text-center">
            <h2 className="text-2xl lg:text-3xl font-extrabold text-[#1a1a2e] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Serving the Greater <span className="text-[#f97316]">Vancouver Area</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto mb-8">
              We proudly serve Vancouver, Burnaby, Richmond, Coquitlam, Surrey, North Vancouver, West Vancouver, and surrounding communities.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {["Vancouver", "Burnaby", "Richmond", "Coquitlam", "Surrey", "North Van", "West Van", "New West"].map((city) => (
                <span key={city} className="bg-white border border-gray-200 rounded-full px-5 py-2.5 text-sm font-semibold text-[#1a1a2e]">
                  {city}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
